/**
 * The item slot — a square well with the art at rest and its actions revealed
 * on hover, plus the grid that lays a pool of them out.
 *
 * ─── Why this is its own module ────────────────────────────────────────────
 * Three pools render the same thing: the paperdoll's carried bag, its hideout
 * stash, and the vendor's two columns. The slot-count growth rule and the
 * explicit empty cell already existed inside `paperdoll.ts` as private
 * functions, so the trade panel could not reuse them — it grew its own tile
 * with a different shape, a different empty state ("Nothing to sell", no grid
 * at all) and a different hover behaviour. One item now looks and behaves the
 * same wherever it is handled, because there is one renderer.
 *
 * ─── Square, and why the count is fixed ────────────────────────────────────
 * The request was WoW's inventory: a square slot showing the item's art, info
 * on hover, and the actions on the item itself. The previous shape was a list
 * ROW — a 26 px icon, the name, the weight and a permanently visible button
 * strip — which is a table with pictures, not a bag.
 *
 * Columns are a fixed count with a matching `max-width`, never `auto-fill`.
 * A bag whose column count follows the window is a different bag after every
 * resize, and "third slot, second row" is the entire reason a slot grid beats
 * a list.
 */

/** Actions are dispatched by the owning panel through one delegated listener. */
export interface SlotAction {
  /** `data-action` — matched by the panel's click handler. */
  action: string;
  label: string;
  /** `go` = the affirmative action (green), `buy` = spending (brass), `danger` = destructive. */
  variant?: 'go' | 'buy' | 'danger';
  disabled?: boolean;
  /** Native `title` on the BUTTON only — explains a disabled action. The slot itself never gets one (see the tooltip note below). */
  title?: string;
}

export interface SlotRender {
  /** `data-item-id` — a `UserItem.id` for an owned item. */
  itemId?: string;
  /** `data-item-key` — an `ItemDef.key` for a catalog entry the player does not own yet. */
  itemKey?: string;
  name: string;
  iconPath: string | null;
  /** Stack size. Rendered only when > 1 — a "1" badge on every slot is noise. */
  quantity?: number;
  /** Short price chip, top-left. Trading only. */
  priceLabel?: string;
  /** The same chip as ready-made HTML (a coin icon and a number). Wins over `priceLabel`; the caller escapes it. */
  priceHtml?: string;
  /** Trading only: a small "N left" badge, bottom-left, so a limited stall reads at a glance. */
  stockLabel?: string;
  /** Broken / worn gear: red border and a desaturated thumb. */
  worn?: boolean;
  actions?: SlotAction[];
  /**
   * `data-tip` — the key the panel's hover handler looks the tooltip content
   * up by. Usually the item id or key; the slot itself does not know how to
   * describe an item, and should not.
   */
  tipId?: string;
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]!);
}

const VARIANT_CLASS: Record<string, string> = {
  go: 'bv-slot-btn-go',
  buy: 'bv-slot-btn-buy',
  danger: 'bv-slot-btn-danger',
};

/**
 * One filled slot.
 *
 * `iconHtml` is passed in rather than resolved here: icon URLs come from
 * `paperdoll.ts`'s `import.meta.glob` map, which is build-time machinery that
 * belongs with the assets, not with the layout. An item with no icon on disk
 * falls back to its name — an empty square would be indistinguishable from an
 * empty slot.
 *
 * NOTE: no `title` attribute anywhere. The native tooltip fires on its own
 * ~1 s OS delay, in OS styling, on top of the card this grid renders — two
 * tooltips for one hover, and the ugly one wins the race often enough to
 * look like a bug.
 */
export function slotHtml(r: SlotRender, iconHtml: string): string {
  const attrs = [
    r.itemId ? `data-item-id="${esc(r.itemId)}"` : '',
    r.itemKey ? `data-item-key="${esc(r.itemKey)}"` : '',
    r.tipId ? `data-tip="${esc(r.tipId)}"` : '',
  ].filter(Boolean).join(' ');

  const art = iconHtml
    ? `<div class="bv-slot-art">${iconHtml}</div>`
    : `<div class="bv-slot-text">${esc(r.name)}</div>`;

  const qty = (r.quantity ?? 1) > 1 ? `<span class="bv-slot-qty">${r.quantity}</span>` : '';
  const price = r.priceHtml
    ? `<span class="bv-slot-price">${r.priceHtml}</span>`
    : (r.priceLabel ? `<span class="bv-slot-price">${esc(r.priceLabel)}</span>` : '');
  const stock = r.stockLabel ? `<span class="bv-slot-stock">${esc(r.stockLabel)}</span>` : '';

  const actions = (r.actions ?? []).map((a) => {
    const cls = `bv-slot-btn${a.variant ? ` ${VARIANT_CLASS[a.variant]}` : ''}`;
    const idAttr = r.itemId ? ` data-item-id="${esc(r.itemId)}"` : '';
    const keyAttr = r.itemKey ? ` data-item-key="${esc(r.itemKey)}"` : '';
    const title = a.title ? ` title="${esc(a.title)}"` : '';
    return `<button type="button" class="${cls}" data-action="${esc(a.action)}"${idAttr}${keyAttr}`
      + `${a.disabled ? ' disabled' : ''}${title}>${esc(a.label)}</button>`;
  }).join('');

  const overlay = actions ? `<div class="bv-slot-actions">${actions}</div>` : '';

  return `<div class="bv-slot bv-slot-filled${r.worn ? ' bv-slot-worn' : ''}" ${attrs}>`
    + `${art}${qty}${price}${stock}${overlay}</div>`;
}

/** An explicitly drawn empty slot. The pool is a grid whether or not it has anything in it. */
export function emptySlotHtml(): string {
  return '<div class="bv-slot bv-slot-empty"></div>';
}

/**
 * How many slots to draw for `itemCount` items.
 *
 * Starts at a full `cols x minRows` block so an empty bag still reads as a
 * bag, then grows by WHOLE ROWS — a jagged last row reads as a rendering
 * fault, and hard-capping would silently hide items, which is the failure
 * this rule exists to avoid.
 */
export function gridSlotCount(itemCount: number, cols: number, minRows: number): number {
  const min = cols * minRows;
  if (itemCount <= min) return min;
  return Math.ceil(itemCount / cols) * cols;
}

/**
 * The grid container's inline style.
 *
 * `--slot-cols` is a LOCAL custom property, not a design token: its value is
 * per-grid and a static stylesheet cannot know it. It carries a fallback
 * (`var(--slot-cols, 5)` in styles.ts) so the rule survives even if a caller
 * forgets to set it — unlike a missing design token, which silently drops the
 * whole declaration. `styles.tokens.test.ts` exempts it and asserts this file
 * still sets it.
 */
export function slotGridStyle(cols: number): string {
  return `--slot-cols:${cols}`;
}

/** Default shape for an owned pool: 5 across, 3 rows showing before it grows. */
export const SLOT_COLUMNS = 5;
export const SLOT_MIN_ROWS = 3;
